import { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import { Loader, useNotify, tokens } from '@hotjar/hotsauce';
import { getParameter } from '@hotjar/insights-common/lib/url';
import { requestAccess, ERRORS } from '@hotjar/webapp/domains/activation/request-access/api';
import { useCurrentUser } from '@hotjar/user';
import { useCurrentSiteId } from '@hotjar/site';
import { SignUpHeader } from '@hotjar/webapp/components/SignUpHeader';
import { AuthenticatedScreen, UnauthenticatedScreen } from './components';
import TraceLineBackground from './assets/trace-line.png';
import { RequestAccessResult } from './types';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  min-width: 300px;
`;

const Content = styled.section`
  ${tokens.flexCenter()};
  flex-grow: 1;
  padding: 0 ${tokens.spacer.x3};
  border-top: 1px solid ${tokens.BRAND_COLORS.cobalt_200};
  background: ${tokens.BRAND_COLORS.cobalt_100} url('${TraceLineBackground}') no-repeat bottom left;

  @media ${tokens.breakpoint.maxWidth.l} {
    background: ${tokens.BRAND_COLORS.cobalt_100};
  }
`;

const ContentBox = styled.div`
  width: 100%;
  max-width: 340px;
`;

const StyledLoader = styled(Loader)`
  margin: auto;
`;

export function PermissionRequiredPage(): React.ReactElement {
  const { data: user, isLoading } = useCurrentUser();
  const siteId = useCurrentSiteId();
  const { showToast } = useNotify();
  const { t, i18n } = useTranslation();
  const currentLocale = i18n.languages?.[0];

  const requestedUrl = getParameter(window.location.href, 'next');
  const [requestAccessResult, setRequestAccessResult] = useState<RequestAccessResult>(
    RequestAccessResult.NOT_SENT,
  );
  const [error, setError] = useState<string>();
  const [isProcessing, setIsProcessing] = useState(false);

  const submitAccessRequest = (email: string, handleInlineSubmitErrors?: (err: string) => void) => {
    if (siteId && !isProcessing) {
      setIsProcessing(true);
      setRequestAccessResult(RequestAccessResult.NOT_SENT);
      requestAccess({
        siteId: parseInt(siteId, 10),
        email,
        url: requestedUrl,
        locale: currentLocale,
      })
        .then(() => setRequestAccessResult(RequestAccessResult.SUCCESS))
        .catch((err) => {
          let errorMessage;
          const errorHandler =
            err.message === ERRORS.ALREADY_HAS_ACCESS
              ? handleInlineSubmitErrors || setError
              : setError;

          if (Object.values(ERRORS).includes(err?.message)) {
            errorMessage = t(`activation:permission-required.error.${err.message}`);
          } else {
            errorMessage = t(`activation:permission-required.error.unknown`);
          }

          errorHandler(errorMessage);
          setRequestAccessResult(RequestAccessResult.ERROR);
        })
        .finally(() => setIsProcessing(false));
    }
  };

  useEffect(() => {
    if (requestAccessResult === RequestAccessResult.ERROR && error) {
      showToast({ text: error, kind: 'danger' });
    }
  }, [requestAccessResult, error, t, showToast]);

  return (
    <Wrapper data-testid="permission-required-page">
      <SignUpHeader user={user} />
      <Content>
        <ContentBox>
          {isLoading ? (
            <StyledLoader size="xl" />
          ) : (
            <>
              {user?.id ? (
                <AuthenticatedScreen
                  user={user}
                  requestAccessResult={requestAccessResult}
                  requestAccess={submitAccessRequest}
                  isProcessing={isProcessing}
                />
              ) : (
                <UnauthenticatedScreen
                  requestAccessResult={requestAccessResult}
                  requestAccess={submitAccessRequest}
                  isProcessing={isProcessing}
                />
              )}
            </>
          )}
        </ContentBox>
      </Content>
    </Wrapper>
  );
}
